import { useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Trash2, MoreVertical, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StudioForm } from "@/components/studio/StudioForm";
import { StudioConnections } from "@/components/studio/StudioConnections";
import { StudioConfigurations } from "@/components/studio/StudioConfigurations";
import { BandwidthTestCard } from "@/components/studio/BandwidthTestCard";
import { BrokerList } from "@/components/studio/BrokerList";
import { ProfileList } from "@/components/studio/ProfileList";
import { StudioDocumentsTab } from "@/components/studio/StudioDocumentsTab";
import {
  useSingleStudio,
  useUpdateStudio,
  useSoftDeleteStudio,
  type StudioInsert,
} from "@/hooks/useStudios";
import { useBridgeStatus } from "@/hooks/useBridgeConnections";
import { useStudioConnections } from "@/hooks/useStudioConnections";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { QueryResult } from "@/components/QueryResult";

const StudioDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: studio, isLoading, isError, error } = useSingleStudio(id);
  const updateStudio = useUpdateStudio();
  const softDelete = useSoftDeleteStudio();
  const { data: bridgeStatus, isError: bridgeError } = useBridgeStatus(
    studio?.bridge_url,
  );
  const { data: studioConnections = [] } = useStudioConnections(studio?.id);

  useSetCurrentEntity("studio", id, studio?.name);

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_STUDIO_TABS = [
    "information",
    "connections",
    "configurations",
    "brokers",
    "profiles",
    "docs",
  ] as const;
  const tabParam = searchParams.get("tab");
  const currentTab =
    tabParam &&
    VALID_STUDIO_TABS.includes(tabParam as (typeof VALID_STUDIO_TABS)[number])
      ? tabParam
      : "information";
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const headerTabs = useMemo(
    () => [
      { value: "information", label: "Information" },
      { value: "connections", label: "Devices" },
      { value: "configurations", label: "Configurations" },
      { value: "brokers", label: "Brokers" },
      { value: "profiles", label: "Profiles" },
      { value: "docs", label: "Docs" },
    ],
    [],
  );

  usePageHeader({
    title: studio?.name,
    tabs: headerTabs,
    currentTab,
    onTabChange: handleTabChange,
  });

  const handleUpdate = (data: StudioInsert) => {
    if (!id) return;
    updateStudio.mutate({ id, studio: data });
  };

  const handleDelete = () => {
    if (!id) return;
    softDelete.mutate(id, {
      onSuccess: () => navigate("/studios"),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading studio...</p>
      </div>
    );
  }

  if (!studio) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={studio}
        entityName="studio"
        backTo="/studios"
        backLabel="Back to Studios"
      >
        {null}
      </QueryResult>
    );
  }

  const getBridgeStatusBadge = () => {
    if (!studio.bridge_url) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          No bridge configured
        </Badge>
      );
    }
    if (bridgeError) {
      return (
        <Badge variant="destructive" className="gap-1">
          <WifiOff className="h-3 w-3" />
          Bridge Offline
        </Badge>
      );
    }
    if (bridgeStatus?.status === "ok") {
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <Wifi className="h-3 w-3" />
          Bridge Online
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        Checking...
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="studio-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/studios")}
            data-testid="studio-back-button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" data-testid="studio-name">
                {studio.name}
              </h1>
              {getBridgeStatusBadge()}
            </div>
            <p className="text-muted-foreground">
              {studio.number}
              {studio.location && ` • ${studio.location}`}
            </p>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              offlineEdit
              onClick={() => setIsDeleteDialogOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Studio?</AlertDialogTitle>
              <AlertDialogDescription>
                This will move the studio to trash. You can restore it later if
                needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        data-testid="studio-tabs"
      >
        <TabsList>
          <TabsTrigger value="information" data-testid="tab-information">
            Information
          </TabsTrigger>
          <TabsTrigger value="connections" data-testid="tab-connections">
            Devices
            {bridgeStatus ? (
              <Badge variant="secondary" className="ml-2 text-xs">
                {bridgeStatus.connections.connected}/
                {bridgeStatus.connections.total}
              </Badge>
            ) : studio ? (
              <Badge variant="secondary" className="ml-2 text-xs">
                {studioConnections.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="configurations" data-testid="tab-configurations">
            Configurations
          </TabsTrigger>
          <TabsTrigger value="brokers">Brokers</TabsTrigger>
          <TabsTrigger value="profiles">Profiles</TabsTrigger>
          <TabsTrigger value="docs">Docs</TabsTrigger>
        </TabsList>

        <TabsContent value="information" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Studio Details</CardTitle>
            </CardHeader>
            <CardContent>
              <StudioForm
                studio={studio}
                onSubmit={handleUpdate}
                onCancel={() => navigate("/studios")}
                isLoading={updateStudio.isPending}
              />
            </CardContent>
          </Card>

          {bridgeStatus && (
            <Card>
              <CardHeader>
                <CardTitle>Bridge Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">
                      {bridgeStatus.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Devices</p>
                    <p className="font-medium">
                      {bridgeStatus.connections.total}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Enabled</p>
                    <p className="font-medium">
                      {bridgeStatus.connections.enabled}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Connected</p>
                    <p className="font-medium">
                      {bridgeStatus.connections.connected}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {studio.bridge_url && bridgeStatus?.status === "ok" && (
            <BandwidthTestCard bridgeUrl={studio.bridge_url} />
          )}
        </TabsContent>

        <TabsContent value="connections" className="mt-6">
          <StudioConnections studio={studio} />
        </TabsContent>

        <TabsContent value="configurations" className="mt-6">
          <StudioConfigurations studio={studio} />
        </TabsContent>

        <TabsContent value="brokers" className="mt-6">
          <BrokerList studioId={studio.id} />
        </TabsContent>

        <TabsContent value="profiles" className="mt-6">
          <ProfileList />
        </TabsContent>

        <TabsContent value="docs" className="mt-6">
          <StudioDocumentsTab studio={studio} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudioDetailPage;
